import type { BlocksOutput } from '@festina/configuration-general-types/configuration'
import type { Tree } from '@festina-life/api'
import {
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useSuspenseQuery } from '@tanstack/react-query'

import { isConfigurationServerService } from '../../../hooks/helpers'
import { type ServerServicesType, useService } from '../../../hooks/useService'
import { fetchBlocksKey } from '../keys'
import { transformPairsToTree } from './helpers'

export const FALLBACK_BLOCK: Tree = {
  name: 'production',
  children: [],
}

export const fetchBlocks = async (
  service: Optional<ServerServicesType>,
): Promise<Optional<BlocksOutput>> => {
  if (!service) {
    return Promise.resolve(null)
  }

  try {
    return await service.api.ConfigurationMetaService.metaServiceGetGetBlocks(
      {},
    )
  } catch (e) {
    if (e.status >= 400 && e.status < 500) {
      return {
        _type: 'BlocksOutput',
        blocks: [FALLBACK_BLOCK.name],
      } satisfies BlocksOutput
    } else {
      throw e
    }
  }
}

export const useFetchBlocks = (server: Optional<ConfigurableServer>) => {
  const service = useService(server)
  const isEnabled = !!server && !!service

  return useSuspenseQuery({
    queryKey: isEnabled
      ? fetchBlocksKey({
          server,
          isFromConfigurationServer: service
            ? isConfigurationServerService(service)
            : false,
        })
      : [],
    queryFn: () => fetchBlocks(service),
    staleTime: 'static',
    select: (data) =>
      transformPairsToTree({
        items: data?.blocks?.filter(notEmpty),
        strictlyMoreGeneralPairs:
          data?.isStrictlyMoreGeneralPairs?.filter(notEmpty),
      }),
  })
}
